import React, { useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, Eye, EyeOff, ArrowLeft, AlertCircle, CheckCircle2, Lock } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiError(detail) {
  if (detail == null) return 'Si è verificato un errore. Riprova.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function ResetPasswordPage() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get('token') || '', [searchParams]);

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!token) {
      setError(language === 'it'
        ? 'Link di reset non valido.'
        : 'Invalid reset link.');
      return;
    }
    if (newPassword.length < 8) {
      setError(language === 'it'
        ? 'La password deve contenere almeno 8 caratteri.'
        : 'Password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(language === 'it'
        ? 'Le password non coincidono.'
        : 'Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: newPassword,
      });
      setSuccess(true);
    } catch (err) {
      console.error('Reset password error:', err);
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col" data-testid="reset-password-page">
      <Navbar onRegisterClick={() => {}} />

      <main className="flex-1 pt-28 pb-16 px-6 flex items-center justify-center">
        <div className="w-full max-w-md">
          <button
            onClick={() => navigate('/login')}
            className="flex items-center gap-2 text-[#A3A3A3] hover:text-white mb-8 transition-colors"
            data-testid="back-to-login-btn"
          >
            <ArrowLeft size={18} />
            {language === 'it' ? 'Torna al login' : 'Back to login'}
          </button>

          <div className="text-center mb-8">
            <h1 className="font-heading font-bold text-2xl lg:text-3xl text-[#E5E5E5] mb-2">
              {language === 'it' ? 'Nuova password' : 'New password'}
            </h1>
            <p className="text-[#A3A3A3]">
              {language === 'it'
                ? 'Scegli una nuova password per il tuo account BOND.'
                : 'Choose a new password for your BOND account.'}
            </p>
          </div>

          <div className="bg-[#0d1117] border border-[#1f2937] rounded-md p-8">
            {success ? (
              <div className="text-center" data-testid="reset-password-success">
                <div className="mx-auto mb-4 w-14 h-14 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37] flex items-center justify-center">
                  <CheckCircle2 size={28} className="text-[#D4AF37]" />
                </div>
                <h2 className="text-lg font-semibold text-[#E5E5E5] mb-3">
                  {language === 'it' ? 'Password aggiornata' : 'Password updated'}
                </h2>
                <p className="text-sm text-[#A3A3A3] mb-6">
                  {language === 'it'
                    ? 'Ora puoi accedere a BOND con la nuova password.'
                    : 'You can now sign in to BOND with your new password.'}
                </p>
                <Button
                  onClick={() => navigate('/login')}
                  className="w-full bg-[#D4AF37] hover:bg-[#B8941F] text-black h-12 rounded-none font-medium"
                  data-testid="go-to-login-success-btn"
                >
                  {language === 'it' ? 'ACCEDI' : 'SIGN IN'}
                </Button>
              </div>
            ) : (
              <>
                {!token && (
                  <div className="mb-6 p-4 bg-red-900/20 border border-red-900/50 rounded flex items-center gap-3 text-red-400" data-testid="missing-token-error">
                    <AlertCircle size={20} />
                    <span className="text-sm">
                      {language === 'it'
                        ? 'Link di reset mancante o non valido. Richiedi un nuovo reset.'
                        : 'Missing or invalid reset link. Request a new reset.'}
                    </span>
                  </div>
                )}

                {error && (
                  <div className="mb-6 p-4 bg-red-900/20 border border-red-900/50 rounded flex items-center gap-3 text-red-400" data-testid="reset-password-error">
                    <AlertCircle size={20} />
                    <span className="text-sm">{error}</span>
                  </div>
                )}

                <form onSubmit={handleSubmit}>
                  <div className="mb-4">
                    <label className="block text-sm text-[#A3A3A3] mb-2">
                      {language === 'it' ? 'Nuova password' : 'New password'}
                    </label>
                    <div className="relative">
                      <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#737373]" />
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="••••••••"
                        className="bg-[#050505] border-[#1f2937] text-[#E5E5E5] h-12 rounded-none pl-12 pr-12 focus:border-[#D4AF37]"
                        required
                        minLength={8}
                        data-testid="new-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-[#A3A3A3] hover:text-white"
                        tabIndex={-1}
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                    <p className="text-xs text-[#737373] mt-2">
                      {language === 'it' ? 'Minimo 8 caratteri.' : 'At least 8 characters.'}
                    </p>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm text-[#A3A3A3] mb-2">
                      {language === 'it' ? 'Conferma password' : 'Confirm password'}
                    </label>
                    <div className="relative">
                      <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#737373]" />
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="••••••••"
                        className="bg-[#050505] border-[#1f2937] text-[#E5E5E5] h-12 rounded-none pl-12 focus:border-[#D4AF37]"
                        required
                        minLength={8}
                        data-testid="confirm-password-input"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading || !token}
                    className="w-full bg-[#D4AF37] hover:bg-[#B8941F] text-black h-12 rounded-none font-medium disabled:opacity-50"
                    data-testid="reset-password-submit"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="animate-spin mr-2" size={18} />
                        {language === 'it' ? 'Aggiornamento...' : 'Updating...'}
                      </>
                    ) : (
                      language === 'it' ? 'REIMPOSTA PASSWORD' : 'RESET PASSWORD'
                    )}
                  </Button>
                </form>

                <div className="mt-6 pt-6 border-t border-[#1f2937] text-center">
                  <p className="text-sm text-[#A3A3A3]">
                    {language === 'it' ? 'Il link è scaduto?' : 'Link expired?'}{' '}
                    <button
                      onClick={() => navigate('/forgot-password')}
                      className="text-[#D4AF37] hover:underline"
                      data-testid="request-new-reset-link"
                    >
                      {language === 'it' ? 'Richiedi un nuovo reset' : 'Request a new reset'}
                    </button>
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
